import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, extractErrorMessage } from "../lib/api";
import { useAuth, hasAccess } from "../context/AuthContext";
import { ChevronLeft, Copy, KeyRound, Check, Send, Calendar, ScrollText, AlertCircle, ShieldCheck, Search, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";

function formatLocalDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function todayLocalDateValue() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function OTCApp() {
  const { user } = useAuth();
  const canWrite = hasAccess(user, "otc", true);

  const [controllers, setControllers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [serial, setSerial] = useState("");
  const [loginDate, setLoginDate] = useState(todayLocalDateValue());
  const [codeIssuedTo, setCodeIssuedTo] = useState("");
  const [reason, setReason] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState("");
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [logQuery, setLogQuery] = useState("");

  const fetchControllers = async () => {
    try {
      const { data } = await api.get("/otc/chambers");
      setControllers(data);
    } catch {
      // graceful fallback to free-text input if endpoint not reachable
    }
  };

  const fetchLogs = async () => {
    try {
      const { data } = await api.get("/otc/logs");
      setLogs(data);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  useEffect(() => {
    fetchControllers();
    fetchLogs();
  }, []);

  const selectedController = useMemo(
    () => controllers.find((c) => String(c.serial_number) === String(serial)),
    [controllers, serial]
  );

  const filteredLogs = useMemo(() => {
    const q = logQuery.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((l) => {
      const hay = [
        String(l.serial_number),
        l.customer || "",
        l.code || "",
        l.code_issued_to || "",
        l.reason || "",
        l.generated_by_name || "",
        l.generated_by_email || "",
        l.login_date || "",
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [logs, logQuery]);

  const onIssue = async (e) => {
    e.preventDefault();
    setError("");
    setIssuing(true);
    try {
      const payload = {
        serial_number: Number(serial),
        login_date: loginDate, // YYYY-MM-DD; backend uses midnight UTC of this date
        code_issued_to: codeIssuedTo.trim(),
        reason: reason.trim(),
      };
      const { data } = await api.post("/otc/issue", payload);
      setIssued(data);
      setCodeIssuedTo("");
      setReason("");
      fetchLogs();
    } catch (err) {
      setError(extractErrorMessage(err));
      setIssued(null);
    } finally {
      setIssuing(false);
    }
  };

  const onCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
        <Link to="/" data-testid="otc-back-link" className="inline-flex items-center gap-1 text-envicom-muted hover:text-envicom-text font-mono text-sm">
          <ChevronLeft className="w-4 h-4" /> Back to tools
        </Link>

        <div className="mt-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded flex items-center justify-center bg-envicom-green/15 text-envicom-green border border-envicom-green/30">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-mono text-2xl md:text-3xl font-bold tracking-tight">One Time Code</h1>
            <p className="text-envicom-muted text-sm">
              Issue a 4-digit engineer-mode access code for a chamber. Valid all day on the chosen date.
            </p>
          </div>
        </div>

        {canWrite ? (
          <section className="mt-8 grid lg:grid-cols-2 gap-6">
            <form onSubmit={onIssue} data-testid="otc-issue-form" className="bg-envicom-surface border border-envicom-border rounded-md p-6">
              <h2 className="font-mono font-bold text-envicom-text">Issue access code</h2>
              <p className="text-envicom-muted text-sm mt-1">
                Enter the chamber serial and the date the engineer needs access on. The generated code is valid all day on that date.
              </p>

              <div className="mt-5 space-y-4">
                <div>
                  <label className="text-envicom-muted text-xs uppercase tracking-wider font-mono">Chamber serial #</label>
                  {controllers.length > 0 ? (
                    <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          role="combobox"
                          aria-expanded={pickerOpen}
                          data-testid="otc-serial-trigger"
                          className="mt-1.5 w-full bg-envicom-input border border-envicom-border rounded px-3 py-2.5 text-left flex items-center justify-between font-mono text-envicom-text hover:border-envicom-borderHover focus:outline-none focus:border-envicom-accent"
                        >
                          {selectedController ? (
                            <span className="truncate">
                              #{selectedController.serial_number} · {selectedController.customer} · {selectedController.chamber_model}
                            </span>
                          ) : (
                            <span className="text-envicom-muted">— Select a chamber —</span>
                          )}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-envicom-muted" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        sideOffset={4}
                        className="p-0 bg-envicom-surface border-envicom-border w-[var(--radix-popover-trigger-width)] max-h-80 overflow-hidden"
                      >
                        <Command className="bg-envicom-surface text-envicom-text">
                          <div className="flex items-center px-3 border-b border-envicom-border">
                            <Search className="w-3.5 h-3.5 text-envicom-muted mr-2 shrink-0" />
                            <CommandInput
                              placeholder="Search by serial, customer, model…"
                              className="font-mono text-sm bg-transparent border-0 focus:ring-0 placeholder:text-envicom-muted/60"
                              data-testid="otc-serial-search-input"
                            />
                          </div>
                          <CommandList className="max-h-64 overflow-y-auto">
                            <CommandEmpty className="py-6 text-center text-envicom-muted text-sm">
                              No chambers found.
                            </CommandEmpty>
                            <CommandGroup>
                              {controllers.map((c) => {
                                const searchValue = `${c.serial_number} ${c.customer} ${c.chamber_model} ${c.location || ""}`;
                                return (
                                  <CommandItem
                                    key={c.id}
                                    value={searchValue}
                                    onSelect={() => {
                                      setSerial(String(c.serial_number));
                                      setPickerOpen(false);
                                    }}
                                    data-testid={`otc-serial-option-${c.serial_number}`}
                                    className="font-mono text-sm cursor-pointer aria-selected:bg-envicom-surfaceHover data-[selected=true]:bg-envicom-surfaceHover"
                                  >
                                    <div className="flex items-center justify-between w-full gap-3">
                                      <div className="flex flex-col leading-tight min-w-0">
                                        <span className="text-envicom-text truncate">#{c.serial_number} · {c.customer}</span>
                                        <span className="text-[11px] text-envicom-muted truncate">{c.chamber_model}{c.location ? ` · ${c.location}` : ""}</span>
                                      </div>
                                      {String(c.serial_number) === String(serial) && <Check className="w-4 h-4 text-envicom-green shrink-0" />}
                                    </div>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <input
                      type="number"
                      required
                      min="1"
                      value={serial}
                      onChange={(e) => setSerial(e.target.value)}
                      data-testid="otc-serial-input"
                      placeholder="Enter chamber serial number (numeric)"
                      className="mt-1.5 w-full bg-envicom-input border border-envicom-border rounded px-3 py-2.5 text-envicom-text focus:outline-none focus:border-envicom-accent font-mono"
                    />
                  )}
                  {selectedController && (
                    <div className="mt-2 text-xs font-mono text-envicom-muted flex flex-wrap gap-x-4 gap-y-1">
                      <span>Customer: <span className="text-envicom-text">{selectedController.customer}</span></span>
                      <span>Model: <span className="text-envicom-text">{selectedController.chamber_model}</span></span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-envicom-muted text-xs uppercase tracking-wider font-mono">Login date</label>
                  <div className="mt-1.5 flex gap-2">
                    <input
                      type="date"
                      required
                      value={loginDate}
                      onChange={(e) => setLoginDate(e.target.value)}
                      data-testid="otc-login-date-input"
                      className="flex-1 bg-envicom-input border border-envicom-border rounded px-3 py-2.5 text-envicom-text focus:outline-none focus:border-envicom-accent font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setLoginDate(todayLocalDateValue())}
                      className="px-3 py-2 rounded border border-envicom-border text-envicom-muted hover:text-envicom-text hover:border-envicom-borderHover font-mono text-xs uppercase tracking-wider"
                      data-testid="otc-login-date-today-btn"
                    >
                      Today
                    </button>
                  </div>
                  <p className="mt-1.5 text-[11px] text-envicom-muted font-mono flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Code is valid the entire calendar day.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-envicom-muted text-xs uppercase tracking-wider font-mono">Code issued to</label>
                    <input
                      type="text"
                      required
                      value={codeIssuedTo}
                      onChange={(e) => setCodeIssuedTo(e.target.value)}
                      data-testid="otc-issued-to-input"
                      placeholder="Engineer name / company"
                      className="mt-1.5 w-full bg-envicom-input border border-envicom-border rounded px-3 py-2.5 text-envicom-text focus:outline-none focus:border-envicom-accent text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-envicom-muted text-xs uppercase tracking-wider font-mono">Reason</label>
                    <input
                      type="text"
                      required
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      data-testid="otc-reason-input"
                      placeholder="e.g. PID tuning, sensor replacement"
                      className="mt-1.5 w-full bg-envicom-input border border-envicom-border rounded px-3 py-2.5 text-envicom-text focus:outline-none focus:border-envicom-accent text-sm"
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div data-testid="otc-error" className="mt-4 px-3 py-2 rounded bg-envicom-error/10 border border-envicom-error/30 text-envicom-error text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={issuing || !serial}
                data-testid="otc-issue-submit-btn"
                className="mt-5 inline-flex items-center gap-2 bg-envicom-accent hover:bg-sky-400 disabled:opacity-50 text-black font-semibold px-4 py-2.5 rounded font-mono"
              >
                <Send className="w-4 h-4" /> {issuing ? "Issuing…" : "Issue code"}
              </button>
            </form>

            <div className="bg-envicom-bg border border-envicom-border rounded-md p-6 flex flex-col">
              {issued ? (
                <div data-testid="otc-issued-panel" className="flex flex-col items-center text-center flex-1 justify-center animate-fade-up">
                  <div className="text-envicom-muted text-[11px] uppercase tracking-[0.22em] font-mono mb-4 flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5 text-envicom-green" /> Code issued
                  </div>
                  <div className="flex gap-2 sm:gap-3 mb-5">
                    {issued.code.split("").map((c, i) => (
                      <div
                        key={i}
                        className="w-14 h-20 sm:w-16 sm:h-24 rounded bg-envicom-surface border border-envicom-border flex items-center justify-center font-mono text-4xl sm:text-5xl font-bold text-envicom-green"
                        style={{ textShadow: "0 0 18px rgba(16,185,129,0.45)" }}
                      >
                        {c}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => onCopy(issued.code)}
                    data-testid="otc-copy-btn"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded border border-envicom-border text-envicom-text hover:border-envicom-borderHover font-mono text-sm"
                  >
                    {copied ? <Check className="w-4 h-4 text-envicom-green" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copied" : "Copy code"}
                  </button>
                  <div className="mt-6 w-full max-w-sm grid grid-cols-2 gap-y-2 text-sm font-mono text-left">
                    <span className="text-envicom-muted">Chamber Serial</span>
                    <span className="text-envicom-text">#{issued.serial_number}</span>
                    <span className="text-envicom-muted">Customer</span>
                    <span className="text-envicom-text">{issued.customer || "—"}</span>
                    <span className="text-envicom-muted">Valid date</span>
                    <span className="text-envicom-text">{formatDate(issued.login_date)}</span>
                    <span className="text-envicom-muted">Issued to</span>
                    <span className="text-envicom-text">{issued.code_issued_to}</span>
                    <span className="text-envicom-muted">Reason</span>
                    <span className="text-envicom-text">{issued.reason}</span>
                  </div>
                  <p className="mt-5 text-[11px] font-mono text-envicom-muted max-w-xs">
                    Code is valid all day on {formatDate(issued.login_date)}.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center flex-1 text-envicom-muted text-sm">
                  <KeyRound className="w-10 h-10 mb-3 opacity-30" />
                  No code issued yet. Fill the form to generate one.
                </div>
              )}
            </div>
          </section>
        ) : (
          <div className="mt-8 p-6 border border-dashed border-envicom-border rounded text-envicom-muted text-sm">
            You have read-only access. Ask the super admin to grant write access to issue codes.
          </div>
        )}

        <section className="mt-10 bg-envicom-surface border border-envicom-border rounded-md overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-envicom-border flex-wrap">
            <div className="flex items-center gap-2 text-envicom-muted text-xs uppercase tracking-wider font-mono">
              <ScrollText className="w-4 h-4" /> Audit log · last 100 codes
            </div>
            <div className="relative ml-auto min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-envicom-muted" />
              <input
                type="search"
                value={logQuery}
                onChange={(e) => setLogQuery(e.target.value)}
                placeholder="Search serial, customer, code, issued to, reason…"
                data-testid="otc-logs-search-input"
                className="w-full bg-envicom-input border border-envicom-border rounded pl-9 pr-3 py-2 text-envicom-text text-sm focus:outline-none focus:border-envicom-accent font-mono"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="otc-logs-table">
              <thead>
                <tr className="bg-envicom-bg/50 text-envicom-muted text-[11px] uppercase tracking-wider font-mono">
                  <th className="text-left p-3">Issued at</th>
                  <th className="text-left p-3">Chamber</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Valid date</th>
                  <th className="text-left p-3">Code</th>
                  <th className="text-left p-3">Issued to</th>
                  <th className="text-left p-3">Reason</th>
                  <th className="text-left p-3">By</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 && (
                  <tr><td colSpan="8" className="p-6 text-center text-envicom-muted">
                    {logQuery ? "No codes match this search." : "No codes issued yet."}
                  </td></tr>
                )}
                {filteredLogs.map((l) => (
                  <tr key={l.id} className="border-t border-envicom-border hover:bg-envicom-surfaceHover/40">
                    <td className="p-3 font-mono text-envicom-text whitespace-nowrap">{formatLocalDateTime(l.created_at)}</td>
                    <td className="p-3 font-mono text-envicom-text">#{l.serial_number}</td>
                    <td className="p-3 text-envicom-muted">{l.customer || "—"}</td>
                    <td className="p-3 font-mono whitespace-nowrap">
                      <div className="text-envicom-text">{formatDate(l.login_date)}</div>
                    </td>
                    <td className="p-3 font-mono text-envicom-green text-base">{l.code}</td>
                    <td className="p-3 text-envicom-text">{l.code_issued_to || "—"}</td>
                    <td className="p-3 text-envicom-muted">{l.reason || "—"}</td>
                    <td className="p-3 text-envicom-text">
                      {l.generated_by_name}
                      <div className="text-[11px] text-envicom-muted">{l.generated_by_email}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
